import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { radarSubmit, radarList, radarTrending } from "@/lib/phase3_api";
import { Input } from "@/components/ui/input";
import { ArrowRight, Broadcast, TrendUp, Clock, Fire } from "@phosphor-icons/react";
import { toast } from "sonner";

function timeAgo(iso) {
  try {
    const secs = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
    if (secs < 60) return `${Math.floor(secs)}s ago`;
    if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
    if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
    return `${Math.floor(secs / 86400)}d ago`;
  } catch { return ""; }
}

function shortHost(u) {
  try {
    return new URL(u).host.replace(/^www\./, "");
  } catch { return u; }
}

export default function RadarPage() {
  const [url, setUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const { data: items = [], isLoading, refetch } = useQuery({
    queryKey: ["radar"],
    queryFn: radarList,
    refetchInterval: 8000,
  });

  const { data: trending = [] } = useQuery({
    queryKey: ["radar-trending"],
    queryFn: radarTrending,
    refetchInterval: 15000,
  });

  const submit = async (e) => {
    e?.preventDefault();
    if (!url.trim()) { toast.error("Paste a SaaS URL"); return; }
    try {
      setSubmitting(true);
      const a = await radarSubmit(url.trim());
      toast.success(a.status === "complete" ? "Cached report" : "Radar sweep queued");
      navigate(`/radar/${a.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Failed");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="radar-page">
      {/* Hero */}
      <section className="border-b border-zinc-200 tech-grid-bg">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 pt-20 pb-16">
          <div className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 mb-4 flex items-center gap-2">
            <Broadcast size={12} /> Public · SaaS Radar
          </div>
          <h1 className="font-display text-5xl sm:text-6xl font-bold tracking-tighter text-zinc-950 leading-[0.95]">
            Every SaaS<br />has a public trail.
          </h1>
          <p className="mt-6 max-w-2xl text-zinc-600 text-base sm:text-lg leading-relaxed">
            Radar reads the target's public surface, tags what shipped, mines what users complain about, and
            keeps a shared changelog every time someone runs the same URL. No sign-up. No auth. Just paste and go.
          </p>
          <form onSubmit={submit} className="mt-10" data-testid="radar-form">
            <div className="flex border border-zinc-950 bg-white">
              <div className="flex items-center px-4 border-r border-zinc-300 font-mono text-xs text-zinc-500 uppercase tracking-wider">
                https://
              </div>
              <Input
                data-testid="radar-url-input"
                value={url}
                onChange={(e) => setUrl(e.target.value.replace(/^https?:\/\//i, ""))}
                placeholder="anthropic.com  ·  posthog.com  ·  your-competitor.com"
                className="sharp flex-1 border-0 focus-visible:ring-0 focus-visible:ring-offset-0 font-mono text-base h-14 px-4"
              />
              <button
                type="submit"
                disabled={submitting}
                data-testid="radar-submit-button"
                className="bg-zinc-950 text-white px-8 flex items-center gap-2 font-mono text-xs uppercase tracking-widest hover:bg-zinc-800 disabled:opacity-60"
              >
                {submitting ? "Sweeping…" : "Radar sweep"} <ArrowRight size={14} weight="bold" />
              </button>
            </div>
            <div className="mt-3 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
              We share one live report per URL — everyone who radars the same site sees the same data.
            </div>
          </form>

          <div className="mt-8 flex flex-wrap gap-3 font-mono text-[11px] uppercase tracking-widest text-zinc-500">
            <Link to="/" className="border border-zinc-300 px-3 py-1.5 hover:bg-zinc-100">
              Private mode →
            </Link>
            <Link to="/lenses" className="border border-zinc-300 px-3 py-1.5 hover:bg-zinc-100">
              Industry Lenses
            </Link>
          </div>
        </div>
      </section>

      {/* Trending */}
      {trending.length > 0 && (
        <section className="border-b border-zinc-200 bg-zinc-50" data-testid="radar-trending">
          <div className="max-w-5xl mx-auto px-6 lg:px-12 py-10">
            <div className="flex items-center gap-2 mb-5">
              <Fire size={16} weight="fill" className="text-amber-600" />
              <h2 className="font-display text-xl font-semibold tracking-tight text-zinc-950">
                Trending · most-swept
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-zinc-200 border border-zinc-200">
              {trending.map((t, idx) => (
                <Link
                  key={t.target_url}
                  to={t.latest_id ? `/radar/${t.latest_id}` : "#"}
                  data-testid={`radar-trending-${idx}`}
                  className="bg-white px-4 py-3 flex items-center gap-3 hover:bg-zinc-50 transition-colors"
                >
                  <span className="font-mono text-lg font-bold text-zinc-300 w-7 shrink-0">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-medium text-zinc-950 truncate">
                      {shortHost(t.target_url)}
                    </div>
                    {t.vertical && (
                      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 truncate">
                        {t.vertical}
                      </div>
                    )}
                  </div>
                  <span className="shrink-0 inline-flex items-center gap-1 border border-amber-300 bg-amber-50 text-amber-800 px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest">
                    <TrendUp size={11} /> {t.sweeps} {t.sweeps === 1 ? "sweep" : "sweeps"}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Feed */}
      <section className="max-w-5xl mx-auto px-6 lg:px-12 py-16">
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="font-display text-2xl font-semibold tracking-tight text-zinc-950">
            Recent sweeps · {items.length}
          </h2>
          <button
            onClick={() => refetch()}
            className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-zinc-900 flex items-center gap-1"
          >
            <TrendUp size={11} /> Refresh
          </button>
        </div>
        {isLoading ? (
          <div className="font-mono text-sm text-zinc-500">Loading…</div>
        ) : items.length === 0 ? (
          <div className="border border-zinc-200 bg-white p-10 text-center">
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">No sweeps yet</div>
            <div className="text-sm text-zinc-700">Be the first — paste a URL above and share the link.</div>
          </div>
        ) : (
          <div className="border border-zinc-200 bg-white">
            {items.map((it, idx) => (
              <Link
                key={it.id}
                to={`/radar/${it.id}`}
                data-testid={`radar-item-${idx}`}
                className={`grid grid-cols-[1fr_auto] gap-4 px-5 py-4 ${idx !== items.length - 1 ? "border-b border-zinc-200" : ""} hover:bg-zinc-50 transition-colors`}
              >
                <div className="min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="font-display font-medium text-zinc-950">{shortHost(it.target_url)}</span>
                    {it.vertical && (
                      <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                        {it.vertical}
                      </span>
                    )}
                    <span className={`font-mono text-[10px] uppercase tracking-widest ${it.status === "complete" ? "text-emerald-700" : it.status === "failed" ? "text-red-700" : "text-amber-700 animate-pulse"}`}>
                      · {it.status}
                    </span>
                  </div>
                  {it.one_line_pitch && (
                    <div className="mt-1 text-sm text-zinc-700 truncate">{it.one_line_pitch}</div>
                  )}
                  {it.diff_summary && (
                    <div className="mt-1 font-mono text-[11px] text-amber-800 truncate">
                      Δ {it.diff_summary}
                    </div>
                  )}
                </div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1 self-center">
                  <Clock size={10} /> {timeAgo(it.updated_at)}
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
